import { memo, useCallback } from "react";

import { Checkbox } from "@/components/ui/checkbox";
import { CommandItem } from "@/components/ui/command";

import type { IShareInviteExtraOptionItemProps } from "./types";

// NOTE: NSAI-6501 — the non-person counterpart of `UserSuggestionItem` (a whole
// project, for instance). Same checkbox semantics and a11y contract: the row is
// the control and `aria-checked` carries the pending-selection state.
const ShareExtraOptionItemComponent = ({
  option,
  icon,
  isSelected,
  onToggle,
  selectAriaLabel,
  unselectAriaLabel,
}: IShareInviteExtraOptionItemProps): JSX.Element => {
  const handleSelect = useCallback((): void => {
    onToggle(option);
  }, [onToggle, option]);

  return (
    <CommandItem
      value={option.value}
      onSelect={handleSelect}
      aria-checked={isSelected}
      aria-label={isSelected ? unselectAriaLabel : selectAriaLabel}
      data-checked={isSelected}
      data-testid={`share-extra-option-${option.id}`}
      className="flex items-center gap-2"
    >
      <Checkbox
        checked={isSelected}
        aria-hidden="true"
        tabIndex={-1}
        className="pointer-events-none shrink-0"
      />
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
        {icon}
      </span>
      <p
        dir="auto"
        className="min-w-0 flex-1 truncate text-sm font-medium rtl:text-right"
      >
        {option.label}
      </p>
    </CommandItem>
  );
};

export const ShareExtraOptionItem = memo(ShareExtraOptionItemComponent);

ShareExtraOptionItem.displayName = "ShareExtraOptionItem";
